import {
  isAllowPreviewImage,
  isAllowPreviewVideo,
  isAllowPreviewAudio,
  isAllowEditDoc,
} from '@/renderer/scripts/composition/allowPreview';
import store from '@/renderer/store/index';

interface StringMap {
  [key: string]: string;
}

// 菜单PV
export const MENU_MAP: StringMap = {
  teamSpace: 'pv_space_company',
  personalSpace: 'pv_space_personal',
  collaborationSpace: 'pv_space_cooperation',
  recycleBin: 'pv_recycle_bin',
  workbench: 'pv_workspace',
  myFavorite: 'pv_collection_list',
  relatedToMe: 'pv_related2me',
  recentlyUsed: 'pv_recent_list',
  linkManage: 'pv_link_list',
  message: 'pv_message_center',
};

export const VERSION_MAP: StringMap = {
  personal: 'version_personal',
  team: 'version_group',
  enterprise: 'version_enterprise',
};

// 文件类操作
export const FILE_ACTIONS_MAP: StringMap = {
  create_folder: 'newfolder',
  create_word: 'new_word',
  create_excel: 'new_excel',
  create_ppt: 'new_ppt',
  rename: 'file_rename',
  move: 'file_move',
  copy: 'file_copy',
  detail: 'file_details',
  edit: 'file_edit',
  history: 'file_history',
  delete: 'file_delete',
  download: 'file_download',
  share: 'file_share',
  preview: 'file_preview',
  addFavorite: 'file_collect',
  delFavorite: 'file_cancel_collect',
  open: 'open',
  invite_to_preview: 'preview_invite',
  invite_to_edit: 'edit_invite',
};

// 预览类型映射
export const PREVIEW_MAP: StringMap = {
  excel: 'doc',
  word: 'doc',
  xls: 'doc',
  xlsx: 'doc',
  image: 'picture',
};

// 操作位置映射
export const LOCATION_MAP: StringMap = {
  personalSpace: 'space_personal',
  teamSpace: 'space_company',
  relatedToMeFileList: 'space_received',
  sendShare: 'space_shared',
  myFavorite: 'space_collected',
  collaborationSpaceList: 'space_group',
  collaborationSpace: 'space_group',
  recentUse: 'recent_list',
  relatedToMe: 'related_to_me',
  workbench: 'workbench',
  linkManage: 'link_list',
  recycleBin: 'recycle_bin',
  suspiciousFile: 'suspicious_file',
};

// 搜索范围映射
export const SCOPE_MAP: StringMap = {
  current: 'current_dir', // 当前文件夹
  all: 'total_dir', // 当前空间
  global: 'global_search', // 全局搜索
};
// 搜索内容映射
export const CONTENT_MAP: StringMap = {
  all: 'all',
  fileName: 'file_name',
  fileContents: 'doc_content',
};
// 搜索类型映射
export const SEARCH_MAP: StringMap = {
  all: 'all',
  dir: 'folder',
  file: 'file',
  word: 'doc',
  excel: 'excel',
  powerpoint: 'ppt',
  pdf: 'pdf',
  text: 'txt',
  image: 'picture',
  video: 'video',
  audio: 'radio',
};

export const Time_MAP: StringMap = {
  all: 'all',
  0: '1day',
  7: '1week',
  30: '1month',
  365: '1year',
};
export const Size_MAP: StringMap = {
  all: 'all',
  '0-1MB': '0～1MB',
  '1-5MB': '1～5MB',
  '5-25MB': '5～25MB',
  '25-100MB': '25～100MB',
  '100MB-1GB': '100MB～1GB',
  '1GB+': '1GB+',
};

const SORT_MAP: StringMap = {
  name: 'filename',
  modificationTime: 'update_time',
  size: 'file_size',
  removalTime: 'delete_time',
  remainingTime: 'rest_time',
};

// 登录方式映射
export const LOGIN_MAP: StringMap = {
  loginWithPhone: 'login_sms',
  loginWithAppCode: 'login_scan',
  loginWithWeCom: 'login_wechat_work',
  loginWithWechat: 'login_wechat',
  loginWithCookie: 'login_cookie',
  loginWithMeeting: 'login_meeting',
};

// 新手引导映射
export const NEWHAND_MAP: StringMap = {
  personalSpace: 'space_personal',
  collaborationSpace: 'space_group',
  teamSpace: 'space_company',
  linkManage: 'share',
  recycleBin: 'delete',
};

export const USER_ROLE_MAP: StringMap = {
  User: 'user',
  Admin: 'admin',
  SuperAdmin: 'superAdmin',
};

// 获得上传列表、下载列表的文件类型
const getFileListType = (fileNameList: string[]) => {
  const typeList = new Set();
  fileNameList.forEach((item: string) => {
    const isPicture = isAllowPreviewImage(item);
    const isVideo = isAllowPreviewVideo(item);
    const isAudio = isAllowPreviewAudio(item);
    const isDoc = isAllowEditDoc(item);
    // eslint-disable-next-line no-nested-ternary
    const type: string = isPicture ? 'picture' : isVideo ? 'video' : isAudio ? 'audio' : isDoc ? 'doc' : 'else';
    typeList.add(type);
  });
  const type = Array.from(typeList).length > 1 ? 'mix' : Array.from(typeList)[0];
  return type;
};

// 获得排序字段信息
const getSortType = (sortInfo: any) => {
  const sort = sortInfo.descending ? 'desc' : 'asc';
  const sortField = SORT_MAP[sortInfo.sortBy] || '';
  return `${sortField}_${sort}`;
};

// 部分上报的eventCode，params特殊处理，与产品定义的字段对齐
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getCodeAndParams = (eventCode: string, params?: any): { reportCode: string; reportParams?: any } => {
  let reportCode = eventCode;
  let reportParams = params || {};
  switch (reportCode) {
    case 'menu_pv':
      reportCode = MENU_MAP[reportParams?.menu] || '';
      reportParams = { version: VERSION_MAP[store.getters.editionFlag] };
      break;
    case 'file_actions':
      reportParams = {
        ...reportParams,
        command: FILE_ACTIONS_MAP[reportParams?.command] || reportParams?.command || '',
        space_module: LOCATION_MAP[reportParams.space_module] || reportParams.space_module,
      };
      break;
    case 'file_preview':
      reportParams = {
        ...reportParams,
        preview_type: PREVIEW_MAP[reportParams?.preview_type] || reportParams?.preview_type || 'unknown_file_type',
      };
      break;
    case 'file_edit':
      reportParams = {
        ...reportParams,
        file_type: PREVIEW_MAP[reportParams?.file_type] || reportParams?.file_type || 'unknown_file_type',
      };
      break;
    case 'delete':
      reportParams = {
        ...reportParams,
        delete_location: LOCATION_MAP[reportParams?.delete_location] || '',
      };
      break;
    case 'authorization':
      reportParams = {
        ...reportParams,
        auth_location: LOCATION_MAP[reportParams?.auth_location] || '',
      };
      break;
    case 'search':
      {
        const searchTypes = params.search_type && params.search_type.map((item: string) => SEARCH_MAP[item]).join(',');
        reportParams = {
          ...reportParams,
          search_catalog: SCOPE_MAP[params.search_catalog],
          search_location: LOCATION_MAP[params.search_location] || '',
          search_for_location: LOCATION_MAP[params.search_for_location] || '',
          search_content: CONTENT_MAP[params.search_content] || '',
          search_type: [...new Set(searchTypes.split(','))].toString() || '',
          search_time: Time_MAP[params.search_time] || 'else',
          search_size: Size_MAP[params.search_size] || 'else',
        };
      }
      break;
    case 'upload':
      // eslint-disable-next-line no-lone-blocks
      {
        if (params.overlap) {
          reportParams = {
            ...reportParams,
            upload_location: LOCATION_MAP[params.upload_location] || '',
          };
        } else {
          const { uploadFileNames } = params;
          const uploadType = getFileListType(uploadFileNames);
          reportParams = {
            ...reportParams,
            upload_location: LOCATION_MAP[params.upload_location] || '',
            upload_type: uploadType,
          };
        }
      }
      break;
    case 'download':
      {
        const { downloadData } = params;
        const downloadType = getFileListType(downloadData.map((item: any) => item.name));
        reportParams = {
          ...reportParams,
          download_location: LOCATION_MAP[params.download_location] || '',
          download_type: downloadType,
        };
      }
      break;
    case 'sort':
      {
        const { sortInfo } = params;
        const sortType = getSortType(sortInfo);
        reportParams = {
          ...reportParams,
          sort_type: sortType,
        };
      }
      break;
    case 'login':
      reportParams = {
        ...reportParams,
        login_type: LOGIN_MAP[params.loginType] || '',
      };
      break;
    case 'newhand':
      reportParams.newhand_guide_show &&
        (reportParams.newhand_guide_show = NEWHAND_MAP[reportParams.newhand_guide_show]);
      reportParams.newhand_guide_clilck &&
        (reportParams.newhand_guide_clilck = NEWHAND_MAP[reportParams.newhand_guide_clilck]);
      reportParams.newhand_guide_skip &&
        (reportParams.newhand_guide_skip = NEWHAND_MAP[reportParams.newhand_guide_skip]);
      break;
    case 'share':
      {
        const { shareFileNameList } = params;
        const shareType = getFileListType(shareFileNameList);
        reportParams = {
          ...reportParams,
          share_type: shareType,
        };
      }
      break;
    default:
      break;
  }
  return { reportCode, reportParams };
};
